import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiSend } from "react-icons/fi";
import { useAskSeV2QuestionMutation } from "../../../../../autogen/bff-api";
import {
  formatDateTime,
  richTextHasContent,
} from "../../../../../common/components/communication/communicationUtils";
import { useApiError } from "../../../../../common/errors/useApiError";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";

export const SupplierAskQuestionForm = ({
  eventId,
  questionDeadline,
}: {
  eventId: string;
  questionDeadline: string;
}) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const [ask, { isLoading }] = useAskSeV2QuestionMutation();

  // Advisory client-side check; the BFF rejects late questions authoritatively (409).
  const closed = new Date(questionDeadline).getTime() <= Date.now();
  const canSubmit = subject.trim().length > 0 && richTextHasContent(content);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await ask({
        eventId,
        createSeV2QuestionRequest: { subject: subject.trim(), content },
      }).unwrap();
      setSubject("");
      setContent("");
      setResetKey((key) => key + 1);
    } catch (error) {
      displayer.trigger(error);
    }
  };

  if (closed) {
    return (
      <Box
        bg="smBackground"
        borderWidth="1px"
        borderColor="smBorder"
        rounded="lg"
        p="4"
      >
        <Text fontWeight="semibold">{t("Questions are closed")}</Text>
        <Text fontSize="sm" color="smMuted" mt="1">
          {t("The deadline for asking questions has passed")} (
          {formatDateTime(questionDeadline)}).
        </Text>
      </Box>
    );
  }

  return (
    <Box
      bg="smBackground"
      borderWidth="1px"
      borderColor="smBorder"
      rounded="lg"
      p="4"
    >
      <Text fontWeight="semibold">{t("Ask a question")}</Text>
      <Text fontSize="sm" color="smMuted" mb="3">
        {t("Question deadline")}: {formatDateTime(questionDeadline)}
      </Text>
      <Stack spacing="3">
        <FormControl isRequired>
          <FormLabel fontSize="sm">{t("Subject")}</FormLabel>
          <Input
            bg="smBackground"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">{t("Question")}</FormLabel>
          <TipTap
            key={resetKey}
            content=""
            editable
            debounceWait={0}
            onUpdate={(value) => setContent(value)}
          />
        </FormControl>
        <Box textAlign="right">
          <Button
            leftIcon={<FiSend />}
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!canSubmit}
          >
            {t("Send question")}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
